/**
 * KÓRE — interacciones de la ficha de producto y del carrito.
 *
 * Se carga UNA vez en todas las páginas desde `snippets/scripts.liquid`, porque su marcado puede
 * llegar después de cargar: el cajón se re-renderiza con un morph del tema y los <script> que
 * vienen en ese HTML no se ejecutan (revisión adversarial, A1).
 *
 * Todo lo que agrega al carrito usa la Ajax API de Shopify y luego avisa al tema con el MISMO
 * evento que dispara su formulario de producto (`CartAddEvent`, que en Ritual 3.x se llama
 * 'cart:update'). Así el cajón se abre solo, se re-renderiza con las secciones que devuelve la
 * misma petición y el contador del ícono se corrige. Ritual 4.x renombra los eventos del
 * carrito: al migrar de versión hay que revisar este archivo contra `@theme/events`.
 *
 * Nunca se usa <form action="/cart/add">: el rastreador de Shopify registraría vistas de
 * variante falsas con esos formularios (L-140).
 *
 * Componentes:
 *  - <kore-agregar>          agregado rápido de una variante (cross-selling y cajón).
 *  - <kore-trio>             trío de adaptógenos: tres frascos en una sola petición.
 *  - <kore-precio-unitario>  precio por cápsula o porción; se re-renderiza al cambiar de variante.
 *  - <kore-entrega>          rango de fechas de entrega estimada en días hábiles, fecha de CDMX.
 *  - <kore-envio-ficha>      estado del envío gratis según el carrito actual.
 *  - Nota de tarjetas        copia el mensaje de la tarjeta de cada kit a la nota del pedido.
 */
import { CartAddEvent, ThemeEvents } from '@theme/events';

/* -------------------------------------------------------------------------------------------
 * Utilidades
 * ----------------------------------------------------------------------------------------- */

/** @returns {{ cart_add_url?: string, cart_url?: string }} */
function rutas() {
  // `Theme` es una constante global de snippets/scripts.liquid (no cuelga de window).
  // eslint-disable-next-line no-undef
  return typeof Theme !== 'undefined' && Theme?.routes ? Theme.routes : {};
}

/**
 * Formatea centavos con el formato de moneda de la tienda (`shop.money_format`).
 * Con `recortar`, un monto en pesos enteros sale sin centavos, como el filtro de Liquid
 * `money_without_trailing_zeros` («$ 48» en vez de «$ 48.00»).
 * @param {number} cents
 * @param {string} [format]
 * @param {{ recortar?: boolean }} [opciones]
 * @returns {string}
 */
export function formatoDinero(cents, format = '${{amount}}', { recortar = false } = {}) {
  const valor = Number(cents) / 100;
  const plantilla = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
  const patron = /\{\{\s*(\w+)\s*\}\}/;
  let token = plantilla.match(patron)?.[1] ?? 'amount';
  if (recortar && Number(cents) % 100 === 0 && !token.includes('no_decimals')) {
    token = token === 'amount' ? 'amount_no_decimals' : token.replace('amount', 'amount_no_decimals');
  }
  /** @param {number} decimales @param {string} miles @param {string} decimal */
  const numero = (decimales, miles, decimal) => {
    const [entero = '0', fraccion] = valor.toFixed(decimales).split('.');
    const conMiles = entero.replace(/\B(?=(\d{3})+(?!\d))/g, miles);
    return fraccion ? `${conMiles}${decimal}${fraccion}` : conMiles;
  };
  /** @type {Record<string, () => string>} */
  const formatos = {
    amount: () => numero(2, ',', '.'),
    amount_no_decimals: () => numero(0, ',', '.'),
    amount_with_comma_separator: () => numero(2, '.', ','),
    amount_no_decimals_with_comma_separator: () => numero(0, '.', ','),
    amount_with_space_separator: () => numero(2, ' ', ','),
    amount_no_decimals_with_space_separator: () => numero(0, ' ', ','),
    amount_with_apostrophe_separator: () => numero(2, "'", '.'),
  };
  return plantilla.replace(patron, (formatos[token] ?? formatos.amount)());
}

/** IDs de las secciones de carrito presentes (cajón y página) para re-renderizarlas de una vez. */
function seccionesDelCarrito() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId ?? '' : ''))
    .filter(Boolean);
}

/** @returns {Promise<{ item_count: number, total_price: number }>} */
export async function leerCarrito() {
  const respuesta = await fetch(`${rutas().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
  if (!respuesta.ok) throw new Error(`cart.js ${respuesta.status}`);
  return respuesta.json();
}

/**
 * Número de piezas del carrito leído del HTML de las secciones que devolvió la misma petición
 * (`[ref="cartItemCount"]`, como hace component-cart-items.js). Ahorra un viaje a /cart.js.
 * @param {Record<string, string> | undefined} secciones
 * @returns {number | null}
 */
function piezasDesdeSecciones(secciones) {
  for (const html of Object.values(secciones ?? {})) {
    if (typeof html !== 'string') continue;
    const nodo = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]');
    const n = Number(nodo?.textContent?.trim());
    if (nodo && Number.isFinite(n)) return n;
  }
  return null;
}

/** Sin respuesta de Shopify (red caída): el texto crudo del navegador sale en inglés. */
const MENSAJE_SIN_CONEXION = 'Hubo un problema de conexión y no se agregó nada al carrito.';

/**
 * Región viva de la página. Se crea al cargar el módulo y no al anunciar: una región que nace
 * junto con su texto suele perderse en VoiceOver (revisión adversarial, re-review M2).
 * @returns {HTMLElement}
 */
function regionDePagina() {
  let region = document.getElementById('kore-anuncios');
  if (!region) {
    region = document.createElement('div');
    region.id = 'kore-anuncios';
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    document.body.appendChild(region);
  }
  return region;
}

/** ¿Hay un <dialog> modal abierto? Un modal vuelve inerte todo lo de afuera (L-148). */
function hayModalAbierto() {
  for (const dialogo of document.querySelectorAll('dialog[open]')) {
    try {
      if (dialogo.matches(':modal')) return true;
    } catch {
      return true; // navegador sin :modal: se asume modal, como el cajón
    }
  }
  return false;
}

/**
 * Anuncia al lector de pantalla exactamente una vez, abra el cajón cuando abra:
 * - siempre en la región de dentro del cajón (`snippets/kore-anuncios`, que el morph no vacía
 *   gracias a `data-skip-subtree-update`); un <dialog> cerrado no está en el árbol de
 *   accesibilidad, así que solo se escucha si el cajón ya está abierto;
 * - en la región de la página solo si en ese momento no hay un modal abierto.
 * El tema abre el cajón con `showModal()` en un requestAnimationFrame, y en un equipo lento puede
 * tardar más; así el texto nunca cae solo en una región inerte (re-review r3, BAJO 3).
 * A los 5 s se borra, como en product-form.js: un texto viejo se leería fuera de contexto al
 * recorrer el cajón (re-review r3, BAJO 2).
 * @param {string} texto
 * @param {string} [llave] - Un aviso con llave reemplaza al anterior de la misma llave (p. ej., el motivo
 *   del aviso del carrito cambió): nunca se leen los dos.
 */
/** @typedef {{ texto: string, llave: string }} Anuncio */
/** @type {Anuncio[]} */
let anunciosPendientes = [];
let anuncioProgramado = false;
/** Los mensajes NUEVOS de la última escritura y cuándo se escribieron (se arrastran una sola vez). */
let ultimoAnuncio = { mensajes: /** @type {Anuncio[]} */ ([]), t: 0 };
// Exportada para la batería (se importa el mismo módulo de la página); el tema la usa por dentro.
export function anunciar(texto, llave = '') {
  if (!texto) return;
  // Lo que se pide en la misma ventana («se agregaron» y un aviso del carrito) se lee junto, sin repetirse.
  const igual = anunciosPendientes.findIndex((m) => m.texto === texto || (llave && m.llave === llave));
  if (igual >= 0) anunciosPendientes[igual] = { texto, llave };
  else anunciosPendientes.push({ texto, llave });
  if (anuncioProgramado) return;
  anuncioProgramado = true;
  window.setTimeout(() => {
    const nuevos = anunciosPendientes;
    anunciosPendientes = [];
    anuncioProgramado = false;
    // Lo escrito hace menos de 1 s quizá no se terminó de leer: sus mensajes nuevos van una vez más, antes
    // de lo nuevo, salvo los que se repiten o los que reemplaza un aviso de la misma llave.
    const previos =
      Date.now() - ultimoAnuncio.t < 1000
        ? ultimoAnuncio.mensajes.filter((m) => !nuevos.some((n) => n.texto === m.texto || (m.llave && n.llave === m.llave)))
        : [];
    const completo = [...previos, ...nuevos].map((m) => m.texto).join(' ');
    /** @type {HTMLElement[]} */
    const regiones = [...document.querySelectorAll('dialog [data-kore-anuncios]')].filter((r) => r instanceof HTMLElement);
    if (!hayModalAbierto()) regiones.push(regionDePagina());
    for (const region of regiones) region.textContent = '';
    // El cambio en otro turno hace que el lector anuncie aunque el texto se repita.
    window.setTimeout(() => {
      for (const region of regiones) region.textContent = completo;
      ultimoAnuncio = { mensajes: nuevos, t: Date.now() };
      window.setTimeout(() => {
        for (const region of regiones) if (region.textContent === completo) region.textContent = '';
      }, 5000);
    }, 60);
  }, 120);
}

/**
 * Piezas del carrito que ve el cliente en el ícono, antes de una petición; si el ícono no las
 * dice (p. ej. más de 99), las del servidor.
 * @returns {Promise<number | null>}
 */
async function piezasActuales() {
  const nodo = document.querySelector('cart-icon [ref="cartBubbleCount"]');
  const n = Number.parseInt(nodo?.textContent?.trim() ?? '', 10);
  if (Number.isFinite(n)) return n;
  try {
    return (await leerCarrito()).item_count ?? null;
  } catch {
    return null;
  }
}

/**
 * Error visible en el propio componente; se limpia solo.
 * @param {Element | null} nodo
 * @param {string} texto
 */
function mostrarError(nodo, texto) {
  if (!(nodo instanceof HTMLElement)) return;
  nodo.textContent = texto;
  window.setTimeout(() => {
    if (nodo.textContent === texto) nodo.textContent = '';
  }, 6000);
}

/**
 * Dispara un evento del carrito desde un nodo conectado. Si el componente salió del DOM a media
 * petición (p. ej. la caja de sugerencias se reemplazó por la confirmación del trío), un evento
 * sobre él nunca llega a `document`: el cajón y el contador quedarían viejos (re-review r5).
 * @param {HTMLElement} origen
 * @param {Event} evento
 */
function emitir(origen, evento) {
  (origen.isConnected ? origen : document).dispatchEvent(evento);
}

/**
 * Agrega líneas al carrito y avisa al tema como lo hace su propio formulario de producto.
 * Si Shopify rechaza la petición (p. ej. 422 por inventario), puede haber agregado una parte:
 * se lee el carrito real y, SOLO si cambió, se dispara el evento con `didError` para que el
 * contador y el cajón se corrijan. Sin cambio no se dispara nada: el tema abre el cajón con
 * cualquier evento del carrito y taparía el error (re-review M6).
 * Con `properties` (el mensaje de la tarjeta de un kit), la misma variante con otras propiedades es otra línea: Shopify
 * las separa, y aquí no se juntan.
 * @param {{ id: number | string, quantity?: number, properties?: Record<string, string> }[]} items
 * @param {HTMLElement} origen - Elemento desde el que se dispara el evento (burbujea a document).
 * @returns {Promise<{ itemCount: number }>}
 */
export async function agregarAlCarrito(items, origen) {
  /** @type {Map<string, { id: number, quantity: number, properties?: Record<string, string> }>} */
  const agrupados = new Map();
  for (const { id, quantity = 1, properties } of items) {
    const variante = Number(id);
    if (!Number.isFinite(variante) || variante <= 0) continue;
    const llave = `${variante}|${properties ? JSON.stringify(properties) : ''}`;
    const previa = agrupados.get(llave);
    if (previa) previa.quantity += Number(quantity) || 1;
    else agrupados.set(llave, { id: variante, quantity: Number(quantity) || 1, ...(properties ? { properties } : {}) });
  }
  if (agrupados.size === 0) throw new Error('Falta elegir los frascos.');

  /** @type {Record<string, unknown>} */
  const cuerpo = { items: [...agrupados.values()] };
  const secciones = seccionesDelCarrito();
  if (secciones.length) {
    cuerpo.sections = secciones.join(',');
    cuerpo.sections_url = window.location.pathname;
  }

  const antes = await piezasActuales();
  let respuesta;
  try {
    respuesta = await fetch(rutas().cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(cuerpo),
    });
  } catch {
    throw new Error(MENSAJE_SIN_CONEXION);
  }
  const datos = await respuesta.json().catch(() => ({}));

  if (!respuesta.ok || datos.status) {
    try {
      const carrito = await leerCarrito();
      if (antes === null || carrito.item_count !== antes) {
        emitir(
          origen,
          new CartAddEvent({}, origen.id || 'kore-ficha', { source: 'kore-ficha', didError: true, itemCount: carrito.item_count ?? 0 })
        );
      }
    } catch {
      /* sin carrito legible, el tema se corrige en la siguiente carga */
    }
    throw new Error(datos.description || datos.message || 'Hubo un problema al agregar al carrito.');
  }

  let itemCount = piezasDesdeSecciones(datos.sections);
  if (itemCount === null) {
    try {
      itemCount = (await leerCarrito()).item_count ?? 0;
    } catch {
      itemCount = [...agrupados.values()].reduce((a, b) => a + b.quantity, 0);
    }
  }

  emitir(
    origen,
    new CartAddEvent({}, origen.id || 'kore-ficha', {
      source: 'kore-ficha',
      itemCount,
      sections: datos.sections,
    })
  );
  return { itemCount };
}

/**
 * Tras un re-render del carrito, si el control que tenía el foco desapareció, lo lleva a un
 * destino estable del mismo carrito (la caja de sugerencias o el botón de cerrar el cajón).
 * @param {HTMLElement} control
 * @param {Element | null} contenedor - El cart-items-component, capturado ANTES del await.
 */
function reubicarFoco(control, contenedor) {
  window.setTimeout(() => {
    if (control.isConnected && document.activeElement === control) return;
    if (document.activeElement && document.activeElement !== document.body && document.activeElement.isConnected) return;
    const destino =
      contenedor?.querySelector('.kore-sugerencias') ??
      contenedor?.closest('dialog')?.querySelector('[ref="closeButton"]') ??
      document.querySelector('cart-drawer-component dialog[open] [ref="closeButton"]');
    if (destino instanceof HTMLElement) destino.focus({ preventScroll: true });
  }, 150);
}

/* -------------------------------------------------------------------------------------------
 * Mensaje de la tarjeta de un kit
 * ----------------------------------------------------------------------------------------- */

/**
 * La propiedad de línea con el mensaje. La manda la ficha de un kit (blocks/kore-tarjeta-kit, mismo nombre) y la página
 * de regalos con <kore-agregar>; el carrito y el pedido la muestran.
 */
export const PROPIEDAD_TARJETA = 'Mensaje de la tarjeta';
/** Las líneas de la nota que escribe este módulo empiezan así; las demás son del cliente y no se tocan. */
const PREFIJO_NOTA = 'Tarjeta · ';

/** @param {unknown} texto */
const limpiarMensaje = (texto) => String(texto ?? '').replace(/\s+/g, ' ').trim();

/**
 * Una línea de nota por cada línea del carrito con mensaje: «Tarjeta · <producto>: <mensaje>», con «× n» si son varias
 * piezas (una tarjeta por kit).
 * @param {any[] | undefined} items - `items` de /cart.js.
 * @returns {string[]}
 */
export function lineasDeTarjeta(items) {
  return (items ?? []).flatMap((item) => {
    const mensaje = limpiarMensaje(item?.properties?.[PROPIEDAD_TARJETA]);
    if (!mensaje) return [];
    const piezas = Number(item.quantity) > 1 ? ` × ${item.quantity}` : '';
    return [`${PREFIJO_NOTA}${item.product_title}${piezas}: ${mensaje}`];
  });
}

/**
 * La nota con las líneas de tarjeta al día: primero lo que escribió el cliente y después las líneas.
 * @param {unknown} nota
 * @param {string[]} lineas
 * @returns {string}
 */
export function notaConTarjetas(nota, lineas) {
  const delCliente = String(nota ?? '')
    .split(/\r?\n/)
    .filter((l) => !l.startsWith(PREFIJO_NOTA))
    .join('\n')
    .trim();
  return [delCliente, ...lineas].filter(Boolean).join('\n');
}

/** ¿La nota ya trae exactamente esas líneas de tarjeta? @param {unknown} nota @param {string[]} lineas */
const notaAlDia = (nota, lineas) =>
  String(nota ?? '')
    .split(/\r?\n/)
    .filter((l) => l.startsWith(PREFIJO_NOTA))
    .join('\n') === lineas.join('\n');

/* -------------------------------------------------------------------------------------------
 * <kore-agregar>: agregado rápido de una variante
 * ----------------------------------------------------------------------------------------- */

/**
 * Marca un botón como ocupado (o lo libera) con los dos atributos que ve el cliente.
 * @param {Element | null} boton
 * @param {boolean} ocupado
 */
function pintarOcupado(boton, ocupado) {
  if (!(boton instanceof HTMLButtonElement)) return;
  boton.disabled = ocupado;
  if (ocupado) boton.setAttribute('aria-busy', 'true');
  else boton.removeAttribute('aria-busy');
}

/**
 * Variantes con un agregado en curso. Vive en el módulo y no en el botón ni en el componente: el
 * morph del carrito le quita al botón `disabled` y `aria-busy` (el HTML del servidor no los trae),
 * y si la fila cambia de lugar descarta la instancia ocupada y crea otra. Sin esto, un segundo clic
 * durante la petición agregaría otra pieza (re-review r3 y r4, BAJO 1).
 * @type {Set<string>}
 */
const variantesEnVuelo = new Set();

/**
 * ¿El carrito trae algo que no se suma con el precio por volumen de una familia? Los descuentos no se
 * combinan y Shopify aplica el mayor (L-154). Compite el volumen de OTRA familia ya aplicado, o un código
 * aplicable (uno de 14–19% le gana al volumen aun con solo adaptógenos), o uno pendiente con el carrito
 * vacío (BixGrow lo deja puesto por URL, medido el 2026-10-15). Un código que el carrito ya rechazó no
 * compite. Con cualquiera de los otros, una cifra prometida puede no cumplirse (L-158).
 * @param {any} carrito - Respuesta de /cart.js.
 * @param {string} tipo - Tipo de producto de la familia (vacío si la familia va por handles).
 * @param {string[]} handles
 * @returns {{ otraFamilia: boolean, conCodigo: boolean }}
 */
function compiteEnCarrito(carrito, tipo, handles) {
  const deEsta = (/** @type {any} */ item) => (tipo ? item.product_type === tipo : handles.includes(item.handle));
  const items = carrito?.items ?? [];
  const otraFamilia = items.some(
    (/** @type {any} */ item) =>
      !deEsta(item) &&
      (item.line_level_discount_allocations ?? []).some((/** @type {any} */ a) =>
        String(a.discount_application?.title ?? '').startsWith('Precio por volumen')
      )
  );
  const codigos = carrito?.discount_codes ?? [];
  const conCodigo = !otraFamilia && (codigos.some((/** @type {any} */ c) => c.applicable) || (codigos.length > 0 && items.length === 0));
  return { otraFamilia, conCodigo };
}


class KoreAgregar extends HTMLElement {
  /** @type {AbortController | undefined} */
  #control;
  /** @type {MutationObserver | undefined} */
  #vigia;

  connectedCallback() {
    this.#control?.abort();
    this.#control = new AbortController();
    // Clic delegado en el elemento y botón habilitado desde el servidor: el morph del carrito
    // conserva este <kore-agregar> (mismo id) y le copia los atributos del HTML nuevo a su botón,
    // así que un `disabled` del servidor lo dejaría muerto para siempre (re-review, ALTO 1).
    // Antes de que el módulo exista, el CSS `kore-agregar:not(:defined)` lo vuelve inerte.
    this.addEventListener(
      'click',
      (evento) => {
        const boton = evento.target instanceof Element ? evento.target.closest('button[data-kore-variante]') : null;
        if (boton instanceof HTMLButtonElement && this.contains(boton)) this.#agregar(boton);
      },
      { signal: this.#control.signal }
    );
    // Ocupado mientras su variante tenga un agregado en curso, aunque el morph le quite los
    // atributos o esta instancia sea nueva.
    this.#vigia?.disconnect();
    this.#vigia = new MutationObserver(() => this.#sincronizar());
    this.#vigia.observe(this, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'data-kore-variante'] });
    this.#sincronizar();
  }

  /** Pinta «ocupado» si la variante del botón tiene un agregado en curso. */
  #sincronizar() {
    const boton = this.querySelector('button[data-kore-variante]');
    if (boton instanceof HTMLButtonElement && !boton.disabled && variantesEnVuelo.has(boton.dataset.koreVariante ?? '')) {
      pintarOcupado(boton, true);
    }
  }

  disconnectedCallback() {
    this.#control?.abort();
    this.#vigia?.disconnect();
  }

  /** @param {HTMLButtonElement} boton */
  async #agregar(boton) {
    const id = boton.dataset.koreVariante;
    if (!id || variantesEnVuelo.has(id)) return;
    // Todo lo que depende del nodo se lee ANTES del await: el morph puede reutilizarlo para otro
    // producto (revisión adversarial, M2).
    const textoAgregado = this.dataset.textoAgregado || 'Agregado al carrito.';
    const error = this.querySelector('[data-kore-error]');
    const contenedor = this.closest('cart-items-component');
    // El mensaje de la tarjeta de un kit (la página de regalos): `data-kore-mensaje` es el id de su campo. Va como lo
    // manda la ficha (recortarTarjeta): con sus saltos de línea, sin espacios en los extremos.
    const campo = this.dataset.koreMensaje ? document.getElementById(this.dataset.koreMensaje) : null;
    const mensaje = campo instanceof HTMLTextAreaElement ? campo.value.trim() : '';
    const properties = mensaje ? { [PROPIEDAD_TARJETA]: mensaje } : undefined;

    variantesEnVuelo.add(id);
    // También cualquier otra tarjeta o fila de la misma variante (cross-selling y cajón).
    for (const otro of document.querySelectorAll(`kore-agregar button[data-kore-variante="${CSS.escape(id)}"]`)) pintarOcupado(otro, true);
    pintarOcupado(boton, true);
    try {
      // Una o varias variantes («id1,id2,id3»): la combinación de la página de inicio y la nota del paquete.
      await agregarAlCarrito(String(id).split(',').filter(Boolean).map((v) => ({ id: v, quantity: 1, ...(properties ? { properties } : {}) })), this);
      // El mensaje ya va con ese kit: el campo se vacía para que el siguiente no lo repita (una tarjeta por kit).
      if (campo instanceof HTMLTextAreaElement && mensaje && campo.value.trim() === mensaje) campo.value = '';
      anunciar(textoAgregado);
      reubicarFoco(boton, contenedor);
    } catch (e) {
      const mensaje = e instanceof Error ? e.message : String(e);
      mostrarError(error, mensaje);
      anunciar(mensaje);
    } finally {
      variantesEnVuelo.delete(id);
      // Se libera todo botón de esa variante: el original y el que haya creado el morph.
      pintarOcupado(boton, false);
      for (const vivo of document.querySelectorAll(`kore-agregar button[data-kore-variante="${CSS.escape(id)}"]`)) {
        pintarOcupado(vivo, false);
      }
    }
  }
}

/* -------------------------------------------------------------------------------------------
 * <kore-trio>: trío de adaptógenos
 * ----------------------------------------------------------------------------------------- */

/**
 * @typedef {{ variante: number, titulo: string, precio: number, imagen: string, alt: string }} OpcionTrio
 */

class KoreTrio extends HTMLElement {
  /** @type {AbortController | undefined} */
  #control;
  /** Petición en curso: un segundo clic no manda otro trío (re-review r3, BAJO 1). */
  #ocupado = false;

  connectedCallback() {
    this.#control?.abort();
    this.#control = new AbortController();
    const { signal } = this.#control;
    // Delegado en el elemento, como <kore-agregar>: un morph que reemplace un select o el botón
    // no deja controles sin escucha.
    this.addEventListener(
      'change',
      (evento) => {
        if (evento.target instanceof HTMLSelectElement && evento.target.matches('[data-kore-trio-slot]')) this.#actualizar();
      },
      { signal }
    );
    this.addEventListener(
      'click',
      (evento) => {
        if (evento.target instanceof Element && evento.target.closest('[data-kore-trio-agregar]')) this.#agregar();
      },
      { signal }
    );
    this.#actualizar();
  }

  disconnectedCallback() {
    this.#control?.abort();
  }

  /**
   * Opciones del trío, leídas en cada uso (el JSON viene del servidor junto con los selects).
   * @returns {Record<string, OpcionTrio>}
   */
  #opciones() {
    try {
      return JSON.parse(this.querySelector('script[data-kore-trio-opciones]')?.textContent || '{}');
    } catch {
      return {};
    }
  }

  /** @param {Record<string, OpcionTrio>} opciones @returns {OpcionTrio[]} */
  #seleccion(opciones) {
    const fija = opciones[this.dataset.fija ?? ''];
    const elegidas = [...this.querySelectorAll('select[data-kore-trio-slot]')]
      .map((select) => (select instanceof HTMLSelectElement ? opciones[select.value] : undefined))
      .filter(Boolean);
    return /** @type {OpcionTrio[]} */ ([fija, ...elegidas].filter(Boolean));
  }

  #actualizar() {
    const boton = this.querySelector('[data-kore-trio-agregar]');
    const opciones = this.#opciones();
    // Sin opciones legibles se conservan los montos de Liquid y el botón queda deshabilitado.
    if (Object.keys(opciones).length === 0) {
      if (boton instanceof HTMLButtonElement) boton.disabled = true;
      return;
    }
    const formato = this.dataset.formatoDinero || '${{amount}}';
    const descuentoPorFrasco = Number(this.dataset.descuento) || 0;
    const seleccion = this.#seleccion(opciones);

    // Miniatura y nombre visible de los lugares 2 y 3.
    for (const select of this.querySelectorAll('select[data-kore-trio-slot]')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const opcion = opciones[select.value];
      if (!opcion) continue;
      const lugar = select.dataset.koreTrioSlot;
      const imagen = this.querySelector(`[data-kore-trio-img="${lugar}"]`);
      if (imagen instanceof HTMLImageElement && opcion.imagen) {
        imagen.src = opcion.imagen;
        imagen.removeAttribute('srcset');
        imagen.alt = opcion.alt || opcion.titulo;
      }
      const nombre = this.querySelector(`[data-kore-trio-nombre="${lugar}"]`);
      if (nombre) nombre.textContent = opcion.titulo;
    }

    const separado = seleccion.reduce((suma, opcion) => suma + opcion.precio, 0);
    const ahorro = seleccion.length === 3 ? descuentoPorFrasco * 3 : 0;
    const total = separado - ahorro;

    const pintar = (/** @type {string} */ selector, /** @type {number} */ valor, recortar = false) => {
      const nodo = this.querySelector(selector);
      if (nodo) nodo.textContent = formatoDinero(valor, formato, { recortar });
    };
    pintar('[data-kore-trio-separado]', separado);
    pintar('[data-kore-trio-total]', total);
    pintar('[data-kore-trio-ahorro]', ahorro, true);
    pintar('[data-kore-trio-total-boton]', total, true);

    if (boton instanceof HTMLButtonElement) boton.disabled = this.#ocupado || seleccion.length !== 3;
  }

  async #agregar() {
    const boton = this.querySelector('[data-kore-trio-agregar]');
    const error = this.querySelector('[data-kore-estado]');
    const seleccion = this.#seleccion(this.#opciones());
    if (!(boton instanceof HTMLButtonElement) || this.#ocupado || boton.disabled || seleccion.length !== 3) return;
    const textoAgregado = this.dataset.textoAgregado || 'Trío agregado al carrito.';

    this.#ocupado = true;
    pintarOcupado(boton, true);
    try {
      await agregarAlCarrito(
        seleccion.map((opcion) => ({ id: opcion.variante, quantity: 1 })),
        this
      );
      anunciar(textoAgregado);
    } catch (e) {
      const mensaje = e instanceof Error ? e.message : String(e);
      mostrarError(error, mensaje);
      anunciar(mensaje);
    } finally {
      this.#ocupado = false;
      pintarOcupado(boton, false);
      this.#actualizar();
    }
  }
}

/* -------------------------------------------------------------------------------------------
 * <kore-volumen>: más frascos, menos por frasco
 * ----------------------------------------------------------------------------------------- */

/**
 * @typedef {{ id: number, nombre: string, precio: number }} VarianteVolumen
 * @typedef {{ id: number, nombre: string, variantes: VarianteVolumen[] }} ProductoVolumen
 * @typedef {{ producto: number, variante: number, tocada: boolean }} Pieza
 */

/**
 * Tarjetas de precio por volumen. La elegida decide cuántas piezas agrega el botón del tema y, en
 * las familias que mezclan productos (adaptógenos), qué producto y presentación lleva cada una.
 * El precio especial lo cobra Shopify (descuentos «Precio por volumen · …»); aquí solo se explica
 * con los montos de `kore.volumen`, recalculados con lo que el cliente elige.
 */
class KoreVolumen extends HTMLElement {
  /** @type {AbortController | undefined} */
  #control;
  /** @type {Pieza[]} */
  #piezas = [];
  #ocupado = false;

  connectedCallback() {
    // En la compra rápida el bloque va oculto (la decisión de volumen vive en la ficha completa): no
    // participa, así no le habla a la barra fija de la página.
    if (this.closest('#quick-add-dialog')) return;
    this.#control?.abort();
    this.#control = new AbortController();
    const { signal } = this.#control;
    this.addEventListener(
      'change',
      (evento) => {
        const objetivo = evento.target;
        if (objetivo instanceof HTMLInputElement && objetivo.classList.contains('kore-volumen__radio')) {
          this.#elegir(Number(objetivo.value));
        } else if (objetivo instanceof HTMLSelectElement && objetivo.dataset.koreVolumenPieza) {
          this.#alCambiarPieza(objetivo);
        }
      },
      { signal }
    );
    this.addEventListener(
      'click',
      (evento) => {
        if (evento.target instanceof Element && evento.target.closest('[data-kore-volumen-combinar]')) this.#combinar();
      },
      { signal }
    );
    // El selector de presentación del tema cambia el precio base y la pieza por defecto.
    (this.closest('.shopify-section') ?? document).addEventListener(ThemeEvents.variantUpdate, this.#alCambiarVariante, {
      signal,
    });
    document.addEventListener(ThemeEvents.cartUpdate, () => this.#revisarCarrito(true), { signal });
    // Un código aplicado en el cajón cambia los descuentos sin un cart:update.
    document.addEventListener(ThemeEvents.discountUpdate, () => this.#revisarCarrito(true), { signal });
    // Volver con «atrás» restaura la página de la caché (bfcache) sin recargarla.
    window.addEventListener('pageshow', (evento) => evento.persisted && this.#revisarCarrito(true), { signal });
    // Estado que calculó el servidor (kore-volumen-compite): sin esperar a /cart.js, el botón y la barra
    // no prometen el total si algo compite. #revisarCarrito lo confirma o lo corrige enseguida.
    this.#otraFamilia = this.dataset.compiteInicial === 'familia';
    this.#conCodigo = this.dataset.compiteInicial === 'codigo';
    this.#elegir(this.cantidad);
    this.#revisarCarrito();
  }

  /**
   * ¿El carrito ya trae algo que compite con este volumen? Los descuentos no se suman: con el volumen
   * de OTRA familia, o con un código y productos de otra familia (el código puede ganar en todo el
   * pedido), las tarjetas no prometen el total.
   */
  #otraFamilia = false;
  #conCodigo = false;
  /** Turno de la última lectura: una respuesta vieja que llega tarde no pisa a la nueva. */
  #turno = 0;
  /** @type {AbortController | undefined} */
  #lectura;

  /**
   * Lee el carrito y pone el aviso. Solo se anuncia si lo disparó un evento del carrito: al llegar, el
   * aviso ya está en la página (lo pinta el servidor) y no se lee de más.
   * @param {boolean} [porEvento]
   */
  async #revisarCarrito(porEvento = false) {
    const turno = ++this.#turno;
    this.#lectura?.abort();
    const lectura = new AbortController();
    this.#lectura = lectura;
    /** @type {any} */
    let carrito;
    try {
      const respuesta = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, { headers: { Accept: 'application/json' }, signal: lectura.signal });
      if (!respuesta.ok) return;
      carrito = await respuesta.json();
    } catch {
      return;
    }
    if (turno !== this.#turno || !this.isConnected) return;
    const handles = (this.dataset.familiaHandles || '').split(',').filter(Boolean);
    const { otraFamilia, conCodigo } = compiteEnCarrito(carrito, (this.dataset.familiaTipo || '').trim(), handles);
    this.#otraFamilia = otraFamilia;
    this.#conCodigo = conCodigo;
    const aviso = this.querySelector('[data-kore-volumen-aviso]');
    if (aviso instanceof HTMLElement) {
      const texto = (/** @type {Element | null} */ nodo) => (nodo?.textContent || '').replace(/\s+/g, ' ').trim();
      const familia = aviso.querySelector('[data-kore-volumen-aviso-familia]');
      const codigo = aviso.querySelector('[data-kore-volumen-aviso-codigo]');
      const antes = aviso.hidden ? '' : texto([familia, codigo].find((s) => s instanceof HTMLElement && !s.hidden) ?? null);
      if (familia instanceof HTMLElement) familia.hidden = !this.#otraFamilia;
      if (codigo instanceof HTMLElement) codigo.hidden = !this.#conCodigo;
      aviso.hidden = !(this.#otraFamilia || this.#conCodigo);
      // Se anuncia lo que cambió por un evento: que aparezca el aviso o que cambie su motivo. Con el cajón
      // modal abierto la página queda inerte: va a la región del cajón (L-148).
      const ahora = aviso.hidden ? '' : texto(this.#otraFamilia ? familia : codigo);
      if (porEvento && ahora && ahora !== antes) anunciar(ahora, 'aviso-volumen');
    }
    this.#actualizar();
  }

  disconnectedCallback() {
    this.#control?.abort();
    this.#lectura?.abort();
  }

  /** Piezas de la tarjeta elegida (1 si no hay ninguna). */
  get cantidad() {
    const radio = this.querySelector('.kore-volumen__radio:checked');
    return radio instanceof HTMLInputElement ? Number(radio.value) || 1 : 1;
  }

  /** @returns {{ min: number, centavos: number }[]} */
  #niveles() {
    try {
      const niveles = JSON.parse(this.querySelector('script[data-kore-volumen-niveles]')?.textContent || '[]');
      return Array.isArray(niveles) ? niveles : [];
    } catch {
      return [];
    }
  }

  /** @returns {ProductoVolumen[]} */
  #catalogo() {
    try {
      const catalogo = JSON.parse(this.querySelector('script[data-kore-volumen-catalogo]')?.textContent || '[]');
      return Array.isArray(catalogo) ? catalogo : [];
    } catch {
      return [];
    }
  }

  /** Descuento por pieza (centavos) para n piezas: el nivel más alto alcanzado. @param {number} n */
  #descuento(n) {
    return this.#niveles().reduce((d, nivel) => (n >= nivel.min && nivel.centavos > d ? nivel.centavos : d), 0);
  }

  /** Precio de una variante del catálogo; la del producto de la ficha sale de data-precio. @param {number} variante */
  #precio(variante) {
    for (const producto of this.#catalogo()) {
      const v = producto.variantes.find((x) => x.id === variante);
      if (v) return v.precio;
    }
    return Number(this.dataset.precio) || 0;
  }

  get #mezcla() {
    return this.dataset.mezcla === 'true';
  }

  /** Pieza por defecto: el producto y la presentación que el cliente ve en la ficha. @returns {Pieza} */
  #piezaBase() {
    return { producto: Number(this.dataset.producto), variante: Number(this.dataset.variante), tocada: false };
  }

  /** @param {number} n */
  #elegir(n) {
    // Conserva lo que el cliente ya eligió y completa con la pieza base. Al volver a 1 se empieza de
    // nuevo: la tarjeta de 1 es la pieza de la ficha y una pieza oculta no debe reaparecer después.
    const piezas = n === 1 ? [] : this.#piezas.slice(0, n);
    while (piezas.length < n) piezas.push(this.#piezaBase());
    this.#piezas = piezas;

    for (const opcion of this.querySelectorAll('[data-kore-volumen-opcion]')) {
      const elegida = Number(opcion.getAttribute('data-kore-volumen-opcion')) === n;
      opcion.classList.toggle('kore-volumen__opcion--elegida', elegida);
      const unidades = opcion.querySelector('[data-kore-volumen-unidades]');
      if (unidades instanceof HTMLElement) {
        unidades.hidden = !elegida;
        if (elegida) this.#pintarPiezas(unidades);
        else unidades.replaceChildren();
      }
    }
    this.#actualizar();
  }

  /** @param {HTMLElement} contenedor */
  #pintarPiezas(contenedor) {
    const catalogo = this.#catalogo();
    if (!this.#mezcla || catalogo.length === 0) return;
    const unidad = this.dataset.unidad || 'frasco';
    const encabezado = document.createElement('p');
    encabezado.className = 'kore-volumen__encabezado';
    encabezado.textContent = `Producto y presentación de cada ${unidad}`;
    const filas = this.#piezas.map((pieza, i) => {
      const fila = document.createElement('div');
      fila.className = 'kore-volumen__pieza';
      const producto = document.createElement('select');
      producto.dataset.koreVolumenPieza = `${i}`;
      producto.dataset.campo = 'producto';
      producto.setAttribute('aria-label', `${unidad[0].toUpperCase()}${unidad.slice(1)} ${i + 1}: producto`);
      for (const p of catalogo) producto.add(new Option(p.nombre, `${p.id}`, false, p.id === pieza.producto));
      const variante = document.createElement('select');
      variante.dataset.koreVolumenPieza = `${i}`;
      variante.dataset.campo = 'variante';
      variante.setAttribute('aria-label', `${unidad[0].toUpperCase()}${unidad.slice(1)} ${i + 1}: presentación`);
      const elegido = catalogo.find((p) => p.id === pieza.producto) ?? catalogo[0];
      for (const v of elegido.variantes) variante.add(new Option(v.nombre, `${v.id}`, false, v.id === pieza.variante));
      fila.append(producto, variante);
      return fila;
    });
    /** @type {Node[]} */
    const nodos = [encabezado, ...filas];
    const companeros = this.#companeros();
    if (companeros.length > 0 && this.#piezas.length > 1) {
      const catalogo = this.#catalogo();
      // Los mismos que pone #combinar, en su orden y sin repetir.
      const nombres = [
        ...new Set(
          this.#piezas.slice(1).map((_, i) => catalogo.find((p) => p.id === companeros[i % companeros.length])?.nombre ?? '')
        ),
      ].filter(Boolean);
      const combinar = document.createElement('button');
      combinar.type = 'button';
      combinar.className = 'kore-volumen__combinar';
      combinar.dataset.koreVolumenCombinar = '';
      combinar.textContent = `Elegir ${nombres.length > 1 ? `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}` : nombres[0]}`;
      if (nombres.length > 0) nodos.push(combinar);
    }
    contenedor.replaceChildren(...nodos);
  }

  /** Compañeros del producto en orden de pedidos reales (metacampo kore.trio). */
  #companeros() {
    return (this.dataset.companeros || '')
      .split(',')
      .map(Number)
      .filter((id) => id > 0 && this.#catalogo().some((p) => p.id === id));
  }

  /** @param {HTMLSelectElement} select */
  #alCambiarPieza(select) {
    const i = Number(select.dataset.koreVolumenPieza);
    const pieza = this.#piezas[i];
    if (!pieza) return;
    const catalogo = this.#catalogo();
    if (select.dataset.campo === 'producto') {
      const anterior = this.#nombreVariante(pieza.variante);
      pieza.producto = Number(select.value);
      const producto = catalogo.find((p) => p.id === pieza.producto);
      // Misma presentación si existe (cápsulas sigue en cápsulas); si no, la primera disponible.
      pieza.variante = (producto?.variantes.find((v) => v.nombre === anterior) ?? producto?.variantes[0])?.id ?? pieza.variante;
      const fila = select.closest('.kore-volumen__pieza');
      const presentacion = fila?.querySelector('select[data-campo="variante"]');
      if (presentacion instanceof HTMLSelectElement && producto) {
        presentacion.replaceChildren(...producto.variantes.map((v) => new Option(v.nombre, `${v.id}`, false, v.id === pieza.variante)));
      }
    } else {
      pieza.variante = Number(select.value);
    }
    pieza.tocada = true;
    this.#actualizar();
  }

  /** @param {number} variante */
  #nombreVariante(variante) {
    for (const producto of this.#catalogo()) {
      const v = producto.variantes.find((x) => x.id === variante);
      if (v) return v.nombre;
    }
    return '';
  }

  #combinar() {
    const companeros = this.#companeros();
    const catalogo = this.#catalogo();
    const presentacion = this.#nombreVariante(Number(this.dataset.variante));
    this.#piezas = this.#piezas.map((pieza, i) => {
      if (i === 0) return pieza;
      const producto = catalogo.find((p) => p.id === companeros[(i - 1) % companeros.length]);
      if (!producto) return pieza;
      const variante = producto.variantes.find((v) => v.nombre === presentacion) ?? producto.variantes[0];
      return { producto: producto.id, variante: variante.id, tocada: true };
    });
    const unidades = this.querySelector('.kore-volumen__opcion--elegida [data-kore-volumen-unidades]');
    if (unidades instanceof HTMLElement) this.#pintarPiezas(unidades);
    this.#actualizar();
    const boton = unidades?.querySelector('[data-kore-volumen-combinar]');
    if (boton instanceof HTMLElement) boton.focus();
  }

  /** @param {Event} evento */
  #alCambiarVariante = (evento) => {
    const detalle = /** @type {any} */ (evento).detail;
    const variante = detalle?.resource;
    const producto = String(detalle?.data?.productId ?? '');
    if (!variante?.id || (producto && producto !== this.dataset.producto)) return;
    this.dataset.variante = `${variante.id}`;
    if (Number.isFinite(Number(variante.price))) this.dataset.precio = `${variante.price}`;
    // Las piezas que el cliente no tocó siguen a la presentación de la ficha.
    for (const pieza of this.#piezas) {
      if (!pieza.tocada && pieza.producto === Number(this.dataset.producto)) pieza.variante = Number(variante.id);
    }
    const unidades = this.querySelector('.kore-volumen__opcion--elegida [data-kore-volumen-unidades]');
    if (unidades instanceof HTMLElement && !unidades.hidden) this.#pintarPiezas(unidades);
    this.#actualizar();
  };

  /** Recalcula las tarjetas, sincroniza el formulario del tema y avisa a la barra fija. */
  #actualizar() {
    const formato = this.dataset.formatoDinero || '${{amount}}';
    const dinero = (/** @type {number} */ c) => formatoDinero(c, formato, { recortar: true }).replace('$ ', '$\u00a0');
    const umbral = Number(this.dataset.umbralReal) || 0;
    const n = this.cantidad;
    const base = Number(this.dataset.precio) || 0;
    // «El segundo, por $X más» = total de esta tarjeta − total MOSTRADO en la anterior. Si esta tarjeta
    // o la anterior llevan piezas con otro precio que la de la ficha, la resta no describe un paso
    // real y se oculta (re-revisiones de código y de regulación de r7).
    let totalPrevio = 0;
    let previoDistinto = false;

    for (const opcion of this.querySelectorAll('[data-kore-volumen-opcion]')) {
      const k = Number(opcion.getAttribute('data-kore-volumen-opcion'));
      const descuento = this.#descuento(k);
      // La tarjeta elegida (2 o más, con mezcla) usa las piezas reales; las demás, k piezas de lo que
      // muestra la ficha. La de 1 siempre es la pieza de la ficha: es la que agrega el tema (medio 1).
      const reales = k === n && n > 1 && this.#mezcla;
      const precios = reales ? this.#piezas.map((p) => this.#precio(p.variante)) : Array(k).fill(base);
      const separado = precios.reduce((a, b) => a + b, 0);
      const total = precios.reduce((a, p) => a + Math.max(p - descuento, 0), 0);
      const ahorro = separado - total;
      const uniforme = precios.every((p) => p === precios[0]);
      const pintar = (/** @type {string} */ sel, /** @type {string} */ texto) => {
        const nodo = opcion.querySelector(sel);
        if (nodo) nodo.textContent = texto;
      };
      pintar('[data-kore-volumen-total]', dinero(total));
      pintar('[data-kore-volumen-separado]', dinero(separado));
      const pct = separado > 0 ? Math.floor((ahorro * 100) / separado) : 0;
      const nodoPct = opcion.querySelector('[data-kore-volumen-pct]');
      if (nodoPct instanceof HTMLElement) {
        nodoPct.hidden = pct <= 0;
        nodoPct.textContent = `−${pct}%`;
      }
      const porPieza = opcion.querySelector('[data-kore-volumen-por-pieza]');
      if (porPieza instanceof HTMLElement) {
        porPieza.hidden = !uniforme;
        porPieza.textContent = `${dinero(precios[0] - descuento)} por ${this.dataset.unidad || 'frasco'}`;
      }
      const envio = opcion.querySelector('[data-kore-volumen-envio]');
      if (envio instanceof HTMLElement) {
        envio.hidden = this.#otraFamilia || this.#conCodigo || !(umbral > 0 && total >= umbral);
        // Sin el precio por pieza (mezcla de precios), el envío va solo y con mayúscula.
        envio.textContent = porPieza instanceof HTMLElement && porPieza.hidden ? 'Con envío gratis' : ' · con envío gratis';
      }
      const distinto = reales && precios.some((p) => p !== base);
      const extra = opcion.querySelector('[data-kore-volumen-extra]');
      if (extra instanceof HTMLElement) {
        extra.hidden = distinto || previoDistinto;
        pintar('[data-kore-volumen-extra-monto]', dinero(total - totalPrevio));
      }
      totalPrevio = total;
      previoDistinto = distinto;
    }

    // El pago exprés del tema solo representa N piezas de la variante elegida en la ficha.
    const mezclado = n > 1 && this.#piezas.some((p) => p.variante !== Number(this.dataset.variante));
    this.dataset.mezclado = `${mezclado}`;
    document.documentElement.toggleAttribute('data-kore-volumen-mezclado', mezclado);
    const formulario = this.closest('.shopify-section')?.querySelector('form[data-type="add-to-cart-form"]');
    const cantidad = formulario?.querySelector('input[name="quantity"]');
    if (cantidad instanceof HTMLInputElement) cantidad.value = `${n}`;

    const total = this.#piezas.reduce((a, p) => a + Math.max(this.#precio(p.variante) - this.#descuento(n), 0), 0);
    const piezas = `${n} ${this.dataset.unidades || 'frascos'}`;
    const resumen = n > 1 ? (this.#otraFamilia || this.#conCodigo ? piezas : `${piezas} · ${dinero(total)}`) : null;
    document.dispatchEvent(new CustomEvent('kore-volumen:cambio', { detail: { cantidad: n, resumen } }));
  }

  /** Agrega las piezas elegidas (lo llama la intercepción del formulario del tema). */
  async agregar() {
    if (this.#ocupado) return;
    const n = this.cantidad;
    const items = this.#mezcla
      ? this.#piezas.map((p) => ({ id: p.variante, quantity: 1 }))
      : [{ id: Number(this.dataset.variante), quantity: n }];
    const error = this.querySelector('[data-kore-volumen-error]');
    const texto = `${n} ${this.dataset.unidades || 'frascos'} agregados al carrito.`;
    this.#ocupado = true;
    this.setAttribute('aria-busy', 'true');
    try {
      await agregarAlCarrito(items, this);
      anunciar(texto);
    } catch (e) {
      const mensaje = e instanceof Error ? e.message : String(e);
      mostrarError(error, mensaje);
      anunciar(mensaje);
    } finally {
      this.#ocupado = false;
      this.removeAttribute('aria-busy');
    }
  }
}

// Con 2 piezas o más, el envío del formulario del tema lo resuelve <kore-volumen>. El tema delega
// sus eventos con un oyente de captura en `document`; uno de captura en `window` corre antes y
// detiene el evento, así que el botón principal, la barra fija y Enter agregan lo elegido.
if (!(/** @type {any} */ (window).__koreVolumenSubmit)) {
  /** @type {any} */ (window).__koreVolumenSubmit = true;
  window.addEventListener(
    'submit',
    (evento) => {
      const formulario = evento.target;
      if (!(formulario instanceof HTMLFormElement) || formulario.dataset.type !== 'add-to-cart-form') return;
      const volumen = formulario.closest('.shopify-section')?.querySelector('kore-volumen');
      if (!(volumen instanceof KoreVolumen) || volumen.cantidad < 2) return;
      evento.preventDefault();
      evento.stopImmediatePropagation();
      volumen.agregar();
    },
    { capture: true }
  );
}

/* -------------------------------------------------------------------------------------------
 * <kore-precio-unitario>: se re-renderiza con la variante elegida
 * ----------------------------------------------------------------------------------------- */

class KorePrecioUnitario extends HTMLElement {
  /** @type {AbortController | undefined} */
  #control;

  connectedCallback() {
    this.#control?.abort();
    this.#control = new AbortController();
    // Igual que product-form.js: la sección, el diálogo de compra rápida o la tarjeta. El
    // diálogo de compra rápida vive fuera de toda sección (revisión adversarial, M1).
    const ambito = this.closest('.shopify-section, dialog, product-card') ?? document;
    ambito.addEventListener(ThemeEvents.variantUpdate, this.#alCambiarVariante, { signal: this.#control.signal });
  }

  disconnectedCallback() {
    this.#control?.abort();
  }

  /** @param {Event} evento */
  #alCambiarVariante = (evento) => {
    const detalle = /** @type {any} */ (evento).detail;
    const html = detalle?.data?.html;
    if (!html?.querySelector) return;
    const producto = String(detalle?.data?.productId ?? '');
    if (producto && this.dataset.producto && producto !== this.dataset.producto) return;
    const nuevo =
      html.querySelector(`kore-precio-unitario[data-bloque="${this.dataset.bloque}"]`) ??
      html.querySelector(`kore-precio-unitario[data-producto="${this.dataset.producto}"]`);
    if (nuevo) this.innerHTML = nuevo.innerHTML;
  };
}

/* -------------------------------------------------------------------------------------------
 * <kore-entrega>: rango de fechas en días hábiles (México)
 * ----------------------------------------------------------------------------------------- */

/**
 * Fecha de hoy en la Ciudad de México, como medianoche UTC del mismo día calendario.
 * Usa formatToParts (no el patrón de texto de una configuración regional, que ya cambió una
 * vez en ICU) y devuelve null si algo no es un número (revisión adversarial, A2).
 * @returns {Date | null}
 */
export function hoyEnCdmx(ahora = new Date()) {
  const partes = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Mexico_City',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(ahora);
  const valor = (/** @type {string} */ tipo) => Number(partes.find((p) => p.type === tipo)?.value);
  const [anio, mes, dia] = [valor('year'), valor('month'), valor('day')];
  if (![anio, mes, dia].every(Number.isFinite)) return null;
  return new Date(Date.UTC(anio, mes - 1, dia));
}

/** Domingo de Pascua (algoritmo anónimo gregoriano), como milisegundos UTC. @param {number} anio */
function pascua(anio) {
  const a = anio % 19;
  const b = Math.floor(anio / 100);
  const c = anio % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31);
  const dia = ((h + l - 7 * m + 114) % 31) + 1;
  return Date.UTC(anio, mes - 1, dia);
}

/**
 * Días sin entregas: descanso obligatorio (LFT art. 74) más Jueves y Viernes Santo.
 * @param {number} anio
 * @returns {Set<number>}
 */
export function festivosMexico(anio) {
  if (!Number.isFinite(anio)) return new Set();
  /** @param {number} mes @param {number} n */
  const lunes = (mes, n) => {
    const primero = new Date(Date.UTC(anio, mes, 1));
    const desfase = (8 - primero.getUTCDay()) % 7;
    return Date.UTC(anio, mes, 1 + desfase + 7 * (n - 1));
  };
  const dia = 86400000;
  const p = pascua(anio);
  const dias = [
    Date.UTC(anio, 0, 1),
    lunes(1, 1), // primer lunes de febrero
    lunes(2, 3), // tercer lunes de marzo
    p - 3 * dia, // Jueves Santo
    p - 2 * dia, // Viernes Santo
    Date.UTC(anio, 4, 1),
    Date.UTC(anio, 8, 16),
    lunes(10, 3), // tercer lunes de noviembre
    Date.UTC(anio, 11, 25),
  ];
  if ((anio - 2024) % 6 === 0) dias.push(Date.UTC(anio, 9, 1)); // transmisión del Poder Ejecutivo
  return new Set(dias);
}

/**
 * Suma días hábiles (sin sábados, domingos ni festivos). Con un tope de iteraciones: una fecha
 * inválida jamás congela la página.
 * @param {Date} desde
 * @param {number} dias
 * @returns {Date | null}
 */
export function sumarDiasHabiles(desde, dias) {
  if (!(desde instanceof Date) || !Number.isFinite(desde.getTime()) || !Number.isFinite(dias)) return null;
  const fecha = new Date(desde.getTime());
  let restantes = dias;
  for (let vueltas = 0; restantes > 0; vueltas++) {
    if (vueltas > 366) return null;
    fecha.setUTCDate(fecha.getUTCDate() + 1);
    const diaSemana = fecha.getUTCDay();
    if (diaSemana === 0 || diaSemana === 6) continue;
    if (festivosMexico(fecha.getUTCFullYear()).has(fecha.getTime())) continue;
    restantes -= 1;
  }
  return fecha;
}

/**
 * "entre el jueves 16 y el martes 21 de octubre" (o con dos meses si cambia de mes).
 * @param {Date} inicio
 * @param {Date} fin
 */
export function rangoDeFechas(inicio, fin) {
  const dia = new Intl.DateTimeFormat('es-MX', { weekday: 'long', day: 'numeric', timeZone: 'UTC' });
  const mes = new Intl.DateTimeFormat('es-MX', { month: 'long', timeZone: 'UTC' });
  const texto = (/** @type {Date} */ d) => dia.format(d).replace(',', '');
  if (inicio.getUTCMonth() === fin.getUTCMonth()) {
    return `entre el ${texto(inicio)} y el ${texto(fin)} de ${mes.format(fin)}`;
  }
  return `entre el ${texto(inicio)} de ${mes.format(inicio)} y el ${texto(fin)} de ${mes.format(fin)}`;
}

class KoreEntrega extends HTMLElement {
  connectedCallback() {
    const minimo = Number(this.dataset.diasMin) || 2;
    const maximo = Number(this.dataset.diasMax) || 5;
    const destino = this.querySelector('[data-kore-entrega-texto]');
    if (!destino || maximo < minimo) return;
    try {
      const hoy = hoyEnCdmx();
      const inicio = hoy && sumarDiasHabiles(hoy, minimo);
      const fin = hoy && sumarDiasHabiles(hoy, maximo);
      if (!inicio || !fin) return; // se queda el texto estático «2 a 5 días hábiles»
      // «Estimada»: es una fecha aproximada (LFPC art. 53-IV), no una promesa.
      destino.textContent = `Entrega estimada ${rangoDeFechas(inicio, fin)}.`;
      this.dataset.calculado = 'true';
    } catch {
      /* se queda el texto estático */
    }
  }
}

/* -------------------------------------------------------------------------------------------
 * <kore-envio-ficha>: envío gratis según el carrito actual
 * ----------------------------------------------------------------------------------------- */

class KoreEnvioFicha extends HTMLElement {
  /** @type {AbortController | undefined} */
  #control;
  #pendiente = 0;

  connectedCallback() {
    this.#control?.abort();
    this.#control = new AbortController();
    if (this.dataset.aplica !== 'true') return;
    const { signal } = this.#control;
    const refrescar = () => this.#refrescar();
    document.addEventListener(ThemeEvents.cartUpdate, refrescar, { signal });
    // Un código aplicado en el cajón cambia el total sin 'cart:update' (revisión, M5).
    document.addEventListener(ThemeEvents.discountUpdate, refrescar, { signal });
    // Volver con «atrás» desde la caché del navegador no reconecta el elemento.
    window.addEventListener('pageshow', (e) => e.persisted && refrescar(), { signal });
    this.#refrescar();
  }

  disconnectedCallback() {
    this.#control?.abort();
  }

  async #refrescar() {
    const turno = ++this.#pendiente;
    let carrito;
    try {
      carrito = await leerCarrito();
    } catch {
      return;
    }
    if (turno !== this.#pendiente) return; // llegó una respuesta más nueva
    this.#pintar(carrito.item_count ?? 0, carrito.total_price ?? 0);
  }

  /** @param {number} piezas @param {number} total */
  #pintar(piezas, total) {
    const destino = this.querySelector('[data-kore-envio-texto]');
    if (!destino) return;
    const formato = this.dataset.formatoDinero || '${{amount}}';
    const real = Number(this.dataset.umbralReal);
    const anunciado = Number(this.dataset.umbralAnunciado);
    const menor = Number(this.dataset.envioMenor);
    if (!real || !anunciado || !menor) return;

    if (piezas === 0) {
      destino.textContent = this.dataset.textoBase || destino.textContent;
    } else if (total >= real) {
      destino.textContent = 'Tu carrito ya tiene envío gratis.';
    } else {
      const unido = (/** @type {string} */ monto) => monto.replace('$ ', '$\u00a0');
      destino.textContent = `Llevas ${unido(formatoDinero(total, formato))} en tu carrito. Te faltan ${unido(
        formatoDinero(anunciado - total, formato)
      )} para el envío gratis; si no, el envío cuesta ${unido(formatoDinero(menor, formato, { recortar: true }))}.`;
    }
  }
}

/* -------------------------------------------------------------------------------------------
 * «Ver opciones» de la barra de envío: baja a las sugerencias del MISMO carrito (cajón o página)
 * ----------------------------------------------------------------------------------------- */

if (!(/** @type {any} */ (window).__koreIrSugerencias)) {
  /** @type {any} */ (window).__koreIrSugerencias = true;
  document.addEventListener('click', (evento) => {
    const boton = evento.target instanceof Element ? evento.target.closest('[data-kore-ir-sugerencias]') : null;
    if (!boton) return;
    const caja = boton.closest('cart-items-component')?.querySelector('.kore-sugerencias');
    if (!(caja instanceof HTMLElement)) return;
    const suave = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    caja.scrollIntoView({ behavior: suave ? 'smooth' : 'auto', block: 'start' });
    caja.focus({ preventScroll: true });
  });
}

/* -------------------------------------------------------------------------------------------
 * <kore-oferta-carrito>: una cifra prometida fuera de la ficha (la combinación de la home, los frascos
 * sueltos de un paquete) también mira el carrito (L-158)
 * ----------------------------------------------------------------------------------------- */

/**
 * Si el carrito trae algo que compite (`compiteEnCarrito`), oculta los `[data-kore-oferta-total]`, cambia
 * la etiqueta de los botones (`data-etiqueta-sin-total`) y muestra `[data-kore-oferta-aviso]`. Revisa al
 * conectar, en cada cart:update y discount:update y al volver con «atrás»; una respuesta vieja de
 * /cart.js que llega tarde no pisa a la nueva. El catálogo de la home agrega por Ajax en la misma página.
 */
class KoreOfertaCarrito extends HTMLElement {
  /** @type {AbortController | undefined} */
  #control;
  /** @type {AbortController | undefined} */
  #lectura;
  #turno = 0;

  connectedCallback() {
    this.#control?.abort();
    this.#control = new AbortController();
    const { signal } = this.#control;
    document.addEventListener(ThemeEvents.cartUpdate, () => this.#revisar(true), { signal });
    document.addEventListener(ThemeEvents.discountUpdate, () => this.#revisar(true), { signal });
    window.addEventListener('pageshow', (evento) => evento.persisted && this.#revisar(true), { signal });
    this.#revisar();
  }

  disconnectedCallback() {
    this.#control?.abort();
    this.#lectura?.abort();
  }

  /**
   * Solo anuncia si lo disparó un evento del carrito, y nunca con `data-kore-oferta-silencio` (la portada:
   * la sección de volumen de la misma página ya anuncia lo mismo, y dos avisos a la vez se pisaban).
   * @param {boolean} [porEvento]
   */
  async #revisar(porEvento = false) {
    const turno = ++this.#turno;
    this.#lectura?.abort();
    const lectura = new AbortController();
    this.#lectura = lectura;
    /** @type {any} */
    let carrito;
    try {
      const respuesta = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, { headers: { Accept: 'application/json' }, signal: lectura.signal });
      if (!respuesta.ok) return;
      carrito = await respuesta.json();
    } catch {
      return;
    }
    if (turno !== this.#turno || !this.isConnected) return;
    const handles = (this.dataset.familiaHandles || '').split(',').filter(Boolean);
    const { otraFamilia, conCodigo } = compiteEnCarrito(carrito, (this.dataset.familiaTipo || '').trim(), handles);
    const compite = otraFamilia || conCodigo;
    this.toggleAttribute('data-compite', compite);
    for (const total of this.querySelectorAll('[data-kore-oferta-total]')) if (total instanceof HTMLElement) total.hidden = compite;
    for (const boton of this.querySelectorAll('[data-etiqueta-sin-total]')) {
      if (!(boton instanceof HTMLElement)) continue;
      const etiqueta = compite ? boton.dataset.etiquetaSinTotal : boton.dataset.etiquetaConTotal;
      if (etiqueta) boton.setAttribute('aria-label', etiqueta);
    }
    const aviso = this.querySelector('[data-kore-oferta-aviso]');
    if (aviso instanceof HTMLElement) {
      const estabaVisible = !aviso.hidden;
      aviso.hidden = !compite;
      if (porEvento && !estabaVisible && compite && !this.hasAttribute('data-kore-oferta-silencio')) {
        anunciar((aviso.textContent || '').replace(/\s+/g, ' ').trim(), 'aviso-oferta');
      }
    }
  }
}

/* -------------------------------------------------------------------------------------------
 * Nota de tarjetas: el mensaje de cada kit también va en la nota del pedido
 * ----------------------------------------------------------------------------------------- */

/** Las líneas de tarjeta del último carrito leído en esta página (null: todavía no se lee). */
let lineasConocidas = /** @type {string[] | null} */ (null);
let colaNota = Promise.resolve();

/**
 * Lleva las líneas de tarjeta a un campo de nota del carrito que no se está editando, y lo guarda con el propio campo
 * del tema (su `input`, que cancela su envío anterior: el último gana). El formulario del carrito manda ese campo al
 * pagar: viejo, borraría las líneas.
 * @param {HTMLTextAreaElement} campo
 */
function reponerLineas(campo) {
  if (lineasConocidas === null || campo === document.activeElement) return;
  const nueva = notaConTarjetas(campo.value, lineasConocidas);
  if (nueva === campo.value) return;
  campo.value = nueva;
  campo.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * Lee el carrito y deja su nota con una línea por cada mensaje de tarjeta, sin tocar lo que escribió el cliente. Una
 * sola cola: dos escrituras a la vez se pisarían. Después repone las líneas en los campos de nota del carrito.
 * @returns {Promise<void>}
 */
export function sincronizarNota() {
  colaNota = colaNota
    .then(async () => {
      const carrito = await leerCarrito();
      const lineas = lineasDeTarjeta(/** @type {any} */ (carrito).items);
      const nota = /** @type {any} */ (carrito).note;
      if (!notaAlDia(nota, lineas)) {
        // Si el cliente está escribiendo la nota, lo suyo sale de ese campo: la nota leída puede ir detrás del último
        // guardado del tema. Si no, de la nota del carrito (un campo sin foco puede ser una copia vieja del cajón).
        const activo = document.activeElement;
        const delCliente = activo instanceof HTMLTextAreaElement && activo.name === 'note' ? activo.value : nota;
        const respuesta = await fetch(rutas().cart_update_url || '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: notaConTarjetas(delCliente, lineas) }),
        });
        if (!respuesta.ok) throw new Error(`cart/update.js ${respuesta.status}`);
      }
      lineasConocidas = lineas;
      for (const campo of document.querySelectorAll('textarea[name="note"]')) if (campo instanceof HTMLTextAreaElement) reponerLineas(campo);
    })
    .catch(() => {
      /* sin red: el siguiente cambio del carrito lo vuelve a intentar */
    });
  return colaNota;
}

/**
 * Solo lee las líneas de tarjeta del carrito, en la misma cola, sin escribir nada: al entrar a editar la nota, una
 * escritura aquí pisaría lo que el cliente empieza a teclear (el tema guarda el campo con cada `input`).
 * @returns {Promise<void>}
 */
function leerLineas() {
  colaNota = colaNota
    .then(async () => {
      lineasConocidas = lineasDeTarjeta(/** @type {any} */ (await leerCarrito()).items);
    })
    .catch(() => {
      /* sin red: al salir del campo se repone con lo último que se conoce */
    });
  return colaNota;
}

/**
 * Un agregado exitoso del formulario de producto ya se llevó el mensaje de la ficha del kit (blocks/kore-tarjeta-kit): su
 * campo se vacía para que el siguiente kit no lo repita (una tarjeta por kit). Con error, el mensaje se queda.
 * @param {Event} evento - `cart:update` del tema.
 */
function vaciarTarjetaDeLaFicha(evento) {
  const datos = /** @type {any} */ (evento).detail?.data;
  const origen = evento.target;
  if (datos?.source !== 'product-form-component' || datos.didError || !(origen instanceof Element)) return;
  // Por su formulario real (`campo.form`), no por el id: un agregado rápido puede pintar otro formulario con el mismo id.
  for (const campo of document.querySelectorAll('.kore-tarjeta-kit__campo')) {
    if (campo instanceof HTMLTextAreaElement && campo.form && origen.contains(campo.form)) campo.value = '';
  }
}

/**
 * Antes de que el formulario de producto del tema arme su envío (`new FormData(form)`): el mensaje de la ficha va sin
 * espacios en los extremos, como lo manda <kore-agregar>, y vacío no va (sin nombre, el campo no entra al envío: Shopify
 * guardaría la propiedad en blanco). Corre en la captura de `window`, antes que la del tema en `document`.
 * @param {Event} evento - `submit`.
 */
function prepararTarjeta(evento) {
  const formulario = evento.target;
  if (!(formulario instanceof HTMLFormElement)) return;
  for (const campo of formulario.elements) {
    if (!(campo instanceof HTMLTextAreaElement) || !campo.classList.contains('kore-tarjeta-kit__campo')) continue;
    campo.value = campo.value.trim();
    if (campo.value) campo.setAttribute('name', `properties[${PROPIEDAD_TARJETA}]`);
    else campo.removeAttribute('name');
  }
}

if (!document.documentElement.hasAttribute('data-kore-nota-tarjetas')) {
  document.documentElement.setAttribute('data-kore-nota-tarjetas', '');
  document.addEventListener(ThemeEvents.cartUpdate, (evento) => {
    vaciarTarjetaDeLaFicha(evento);
    void sincronizarNota();
  });
  window.addEventListener('submit', prepararTarjeta, { capture: true });
  // Un carrito que ya trae mensajes (agregados en otra visita, sin evento del carrito o con la red caída), o una nota con
  // líneas de tarjeta (de un kit que quizá ya se quitó, en otra pestaña), se repasa al cargar.
  const conMensaje = [...document.querySelectorAll('.cart-items__properties dt')].some((dt) => dt.textContent?.trim() === `${PROPIEDAD_TARJETA}:`);
  const conLineas = [...document.querySelectorAll('textarea[name="note"]')].some((c) => c instanceof HTMLTextAreaElement && c.value.split(/\r?\n/).some((l) => l.startsWith(PREFIJO_NOTA)));
  if (conMensaje || conLineas) void sincronizarNota();
  // El cliente edita la nota en el carrito: al entrar se leen (sin escribir) las líneas del carrito, que otra pestaña
  // pudo cambiar, y al salir vuelven las que haya borrado, cuando termine esa lectura.
  document.addEventListener('focusin', (evento) => {
    if (evento.target instanceof HTMLTextAreaElement && evento.target.name === 'note') void leerLineas();
  });
  document.addEventListener('focusout', (evento) => {
    const campo = evento.target;
    if (!(campo instanceof HTMLTextAreaElement) || campo.name !== 'note') return;
    void (lineasConocidas === null ? sincronizarNota() : colaNota).then(() => reponerLineas(campo));
  });
}

/* -------------------------------------------------------------------------------------------
 * Registro (idempotente)
 * ----------------------------------------------------------------------------------------- */

/** @type {[string, CustomElementConstructor][]} */
const componentes = [
  ['kore-agregar', KoreAgregar],
  ['kore-trio', KoreTrio],
  ['kore-precio-unitario', KorePrecioUnitario],
  ['kore-entrega', KoreEntrega],
  ['kore-envio-ficha', KoreEnvioFicha],
  ['kore-volumen', KoreVolumen],
  ['kore-oferta-carrito', KoreOfertaCarrito],
];
for (const [nombre, clase] of componentes) {
  if (!customElements.get(nombre)) customElements.define(nombre, clase);
}

// La región viva de la página existe desde la carga, antes del primer anuncio.
regionDePagina();
